import { TourStatusEnum } from 'api-hooks/tour/common';
import {
  TourLiteModel,
  TourModel,
  useGetTour,
  useGetTours,
} from 'api-hooks/tour/tour-list/tour-detail';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { ModelTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: TourModel | TourLiteModel): Option {
  return {
    id: item.id,
    label: `${item.type?.toUpperCase()} - ${item.code} - ${item.name}`,
  };
}

interface Props {
  name: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: TourModel) => void;
  noMargin?: boolean;
  onAfterSelectItem?: (prevValue?: any, value?: any) => void;
}

export default function TourListSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:tour'), placeholder = t('common:tour') } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('tours');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (_value) => {
      setValue(props.name, _value[0]?.id, {
        shouldTouch: true,
        shouldValidate: true,
      });
      props.onAfterSelectItem?.(value, _value[0]?.id);
    },
    useListQueryHook: useGetTours,
    useDetailLazyQueryHook: useGetTour,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: {
            status: `${TourStatusEnum.active}`,
          },
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.TourCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),

    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => {
        const newResult = ModelTransformer(v, TourLiteModel);
        return transformer(newResult);
      });
    }, []),
    detailTransformer: React.useCallback((result) => {
      const newResult = ModelTransformer(result.data, TourModel);
      props.onAfterDetailChange?.(newResult);
      return transformer(newResult);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
